import { useCallback, useEffect, useState } from 'react';

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

export interface AsyncOptions {
  /**
   * Lädt neu, sobald der Tab bzw. die installierte App wieder in den
   * Vordergrund kommt. NUR für Anzeige-Seiten (Tabellen/Übersichten)
   * einschalten — auf Formularseiten würde das Nachladen die noch nicht
   * gespeicherten Eingaben überschreiben, da die Drafts aus den geladenen
   * Daten initialisiert werden.
   */
  refreshOnFocus?: boolean;
}

/**
 * Lädt Daten via async-Funktion, kümmert sich um loading/error/abort.
 * `deps` steuert Neuladen analog zu useEffect.
 */
export function useAsync<T>(
  fn: (signal: AbortSignal) => Promise<T>,
  deps: ReadonlyArray<unknown>,
  options: AsyncOptions = {},
): AsyncState<T> {
  const { refreshOnFocus = false } = options;
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fn(controller.signal)
      .then((result) => {
        if (!controller.signal.aborted) setData(result);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err.message : 'Unbekannter Fehler');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  // Rückkehr in den Vordergrund => frische Daten. In der iOS-Home-Bildschirm-App
  // gibt es kein Neuladen per Tastendruck; ohne das hier zeigt die App beliebig
  // alte Werte, sobald sie im Hintergrund lag.
  useEffect(() => {
    if (!refreshOnFocus) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') reload();
    };
    document.addEventListener('visibilitychange', onVisible);
    // Safari feuert visibilitychange beim App-Wechsel nicht immer — focus ergänzt es.
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [refreshOnFocus, reload]);

  return { data, loading, error, reload };
}
